import {
    Component,
    Input,
    Output,
    EventEmitter,
    OnInit,
    OnDestroy,
    OnChanges,
    SimpleChanges,
    effect,
    inject,
} from '@angular/core'
import { Subject, Subscription } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { LeadsService } from '../../../../features/crm/services/lead.service'
import { LeadService } from '../../../../features/crm/services/lead-info.service'
import { IDashboardService } from '@core/services/dashboard/dashboard.service'
import {
    IRMPerformanceData,
    IUsersLeadsApiData,
    IUserLeadItemApi,
} from '../../../../features/crm/models/lead.model'
import { TEAM_ROLE_LABELS } from '@app/core/constants/app.constants'
import { ELeadStageTitle } from '@app/core/enum/client.enum'
import { mapStageNameToStage } from '../../../../features/crm/helpers/lead-list.helper'
import { fetchAllUsersLeadsWithProducts } from '../../../../core/helpers/leads-data.helper'
import { API_ERRORS } from '@app/core/constants/api-errors.constants'
import { getAchievementColorClass } from '@app/core/helpers/achievement-color.helper'
import { LoadingStateComponent } from '../../../../shared/components/loading-state/loading-state.component'
import { SvgIconComponent } from '../../../../shared/components/svg-icon/svg-icon.component'
import { TitleCasePipe } from '@angular/common'

@Component({
    selector: 'app-rm-performance-dashboard',
    templateUrl: './rm-performance-dashboard.component.html',
    styleUrls: ['./rm-performance-dashboard.component.scss'],
    imports: [LoadingStateComponent, SvgIconComponent, TitleCasePipe],
})
export class RMPerformanceDashboardComponent implements OnInit, OnDestroy, OnChanges {
    private leadsService = inject(LeadsService)
    private leadInfoService = inject(LeadService)
    private dashboardService = inject(IDashboardService)

    @Input() zmUserId = ''
    @Input() height = '600px'
    @Input() title = "Managers' Performance"
    @Output() rmNamesChange = new EventEmitter<string[]>()

    rmPerformanceData: IRMPerformanceData[] = []
    isLoading = false
    errorMessage = ''
    hasError = false

    private destroy$ = new Subject<void>()
    private rmPerformanceSubscription?: Subscription
    private lastAcademicYearStart: number | null = null

    constructor() {
        effect(() => {
            const academicYearStart = this.dashboardService.selectedAcademicYearStart()
            if (this.lastAcademicYearStart === null) {
                this.lastAcademicYearStart = academicYearStart
                return
            }
            if (academicYearStart === this.lastAcademicYearStart) {
                return
            }
            this.lastAcademicYearStart = academicYearStart
            if (this.zmUserId) {
                this.loadRMPerformanceData()
            }
        })
    }

    ngOnInit(): void {
        if (this.zmUserId) {
            this.loadRMPerformanceData()
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['zmUserId'] && !changes['zmUserId'].firstChange && this.zmUserId) {
            this.loadRMPerformanceData()
        }
    }

    ngOnDestroy(): void {
        this.unsubscribe()
        this.destroy$.next()
        this.destroy$.complete()
    }

    private unsubscribe(): void {
        if (this.rmPerformanceSubscription) {
            this.rmPerformanceSubscription.unsubscribe()
            this.rmPerformanceSubscription = undefined
        }
    }

    private loadRMPerformanceData(): void {
        if (!this.zmUserId) {
            return
        }

        this.isLoading = true
        this.hasError = false
        this.errorMessage = ''
        this.unsubscribe()

        this.rmPerformanceSubscription = fetchAllUsersLeadsWithProducts(
            this.zmUserId,
            this.leadsService,
            this.leadInfoService,
            this.dashboardService.selectedAcademicYearStart(),
        )
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: ([data, productMap]: [IUsersLeadsApiData, Map<number, string>]) => {
                    try {
                        this.rmPerformanceData = this.calculateRMPerformance(
                            data,
                            productMap,
                            this.zmUserId,
                        )
                        const rmNames = this.rmPerformanceData.map((manager) => manager.rmName)
                        this.rmNamesChange.emit(rmNames)
                        this.isLoading = false
                    } catch (error) {
                        this.hasError = true
                        this.errorMessage = API_ERRORS.UNEXPECTED_RESPONSE
                        this.isLoading = false
                    }
                },
                error: () => {
                    this.hasError = true
                    this.errorMessage = API_ERRORS.FETCH_FAILED
                    this.isLoading = false
                    this.rmPerformanceData = []
                },
            })
    }

    private calculateRMPerformance(
        data: IUsersLeadsApiData,
        productMap: Map<number, string>,
        zmUserId: string,
    ): IRMPerformanceData[] {
        if (!data.user_leads?.length) {
            return []
        }

        const performanceData: IRMPerformanceData[] = []

        const normalizedZmUserId = this.normalizeLeadUserId(zmUserId)
        const reportingMembers = data.user_leads.filter((userLead) => {
            if (
                normalizedZmUserId &&
                this.normalizeLeadUserId(userLead.user_id) === normalizedZmUserId
            ) {
                return false
            }
            return !this.isZonalManagerRole(userLead.role_name)
        })

        for (const userLead of reportingMembers) {
            const leads = userLead.leads || []
            const totalLeads = userLead.total_leads ?? leads.length

            const pipelineStages: ELeadStageTitle[] = [
                ELeadStageTitle.S3,
                ELeadStageTitle.S4,
                ELeadStageTitle.S5,
                ELeadStageTitle.S6,
            ]
            const pipeline = leads.filter((leadItem: IUserLeadItemApi) => {
                const stage = mapStageNameToStage(leadItem.current_stage?.stage_name ?? '')
                return pipelineStages.includes(stage)
            }).length

            const won = leads.filter((leadItem: IUserLeadItemApi) => {
                const stage = mapStageNameToStage(leadItem.current_stage?.stage_name ?? '')
                return stage === ELeadStageTitle.S7
            }).length

            const conversionPercentage = totalLeads
                ? Math.round((won / totalLeads) * 100 * 100) / 100
                : 0

            performanceData.push({
                rmId: userLead.user_id || '',
                rmName: userLead.user_name || '-',
                leads: totalLeads,
                pipeline,
                won,
                conversionPercentage,
            })
        }

        return performanceData.sort((left, right) => {
            if (right.conversionPercentage !== left.conversionPercentage) {
                return right.conversionPercentage - left.conversionPercentage
            }
            return left.rmName.localeCompare(right.rmName)
        })
    }

    getProgressBarWidth(percentage: number): number {
        return Math.max(0, Math.min(100, percentage))
    }

    readonly getAchievementColorClass = getAchievementColorClass

    private normalizeLeadUserId(id: string | null | undefined): string {
        return String(id ?? '').trim()
    }

    private isZonalManagerRole(roleName: string | null | undefined): boolean {
        const trimmedRole = roleName?.trim()
        if (!trimmedRole) {
            return false
        }
        return trimmedRole.toLowerCase() === TEAM_ROLE_LABELS.ZONAL_MANAGER.toLowerCase()
    }
}
